import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SportChips, { useSportNames } from "../../components/SportChips";
import toast from "react-hot-toast";
import api from "../../services/api";
import { getUsers, deleteUser, purgeDemoAthletes, type User } from "../../services/users";
import type { Club } from "../../services/clubs";
import LoginAsButton from "../../components/LoginAsButton";
import Pagination, { usePagination } from "../../components/Pagination";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { formatDate } from "../../lib/dateFormat";

// Athlete account plans (from athlete_subscriptions.plan_code). "elita" is the
// stored code for the tier the UI labels "Elite".
const PLAN_META: Record<string, { label: string; cls: string }> = {
  free: { label: "Free", cls: "bg-slate-100 text-slate-600 ring-1 ring-slate-200" },
  start: { label: "Start", cls: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
  campion: { label: "Campion", cls: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  elita: { label: "Elite", cls: "bg-violet-50 text-violet-700 ring-1 ring-violet-200" },
};

function planMeta(code?: string | null) {
  return PLAN_META[(code || "free").toLowerCase()] || PLAN_META.free;
}

// Whole-year age from a date-of-birth ISO string. Returns null when missing or
// out of a sane range so we simply don't render an age chip.
function computeAge(dob?: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 120 ? age : null;
}

export default function Athletes() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState<User[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [clubFilter, setClubFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  // Demo-athlete purge: preview holds the dry-run result (count + emails);
  // `purging` guards the confirm button while the delete runs.
  const [demoPreview, setDemoPreview] = useState<{ count: number; emails: string[] } | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    fetchAthletes();
    // Load the platform's clubs so the dropdowns can list every option,
    // not just what's already visible in the current athlete rows.
    (async () => {
      try {
        const { data } = await api.get<Club[]>("/clubs");
        // /clubs may return {club, role}[] for non-platform-admins.
        const flat = Array.isArray(data)
          ? data.map((row: any) => row?.club ?? row).filter(Boolean)
          : [];
        setClubs(flat);
      } catch (err) {
        console.error("Failed to load clubs for filters:", err);
      }
    })();
  }, []);

  const fetchAthletes = async () => {
    try {
      setLoading(true);
      // pageSize=500 so we get the full club roster in one page —
      // filters (search / club / sport / city) run client-side over
      // the loaded set, and the old default of 20 silently cut off
      // any athlete past row 20.
      const response = await getUsers({ role: "athlete", pageSize: 500 });
      setAthletes(response.data);
    } catch (error) {
      console.error("Failed to fetch athletes:", error);
      toast.error("Nu s-au putut încărca sportivii");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId: string) => {
    if (deleteConfirmId === userId) {
      // Second click - proceed with delete
      handleDeleteConfirm(userId);
    } else {
      // First click - show confirmation
      setDeleteConfirmId(userId);
      setTimeout(() => setDeleteConfirmId(null), 3000); // Reset after 3 seconds
    }
  };

  // Open the demo-purge dialog by first fetching a dry-run preview so the
  // admin sees exactly how many (and which) accounts will be removed.
  const openDemoPurge = async () => {
    setDemoLoading(true);
    try {
      const preview = await purgeDemoAthletes(true);
      setDemoPreview({ count: preview.count, emails: preview.emails || [] });
    } catch (e) {
      console.error("Failed to preview demo athletes:", e);
      toast.error("Nu s-a putut încărca lista conturilor demo");
    } finally {
      setDemoLoading(false);
    }
  };

  const confirmDemoPurge = async () => {
    setPurging(true);
    try {
      const res = await purgeDemoAthletes(false);
      toast.success(`Am șters ${res.deleted} cont(uri) demo`);
      setDemoPreview(null);
      fetchAthletes();
    } catch (e) {
      console.error("Failed to purge demo athletes:", e);
      toast.error("Ștergerea conturilor demo a eșuat");
    } finally {
      setPurging(false);
    }
  };

  const handleDeleteConfirm = async (userId: string) => {
    try {
      await deleteUser(userId);
      toast.success("Sportiv șters cu succes!");
      setDeleteConfirmId(null);
      fetchAthletes();
    } catch (error: any) {
      console.error("Failed to delete athlete:", error);
      toast.error(error.response?.data?.error || "Nu s-a putut șterge sportivul");
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAthletes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAthletes.map(a => a.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setBulkDeleting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of Array.from(selectedIds)) {
      try {
        await deleteUser(id);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    setBulkDeleting(false);
    setShowBulkConfirm(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      toast.success(`${successCount} sportiv(i) șters(i) cu succes!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} sportiv(i) nu au putut fi șterși`);
    }

    fetchAthletes();
  };

  // Derive filter option lists from the clubs response so the
  // dropdowns show every possible value (not just what's currently
  // visible). Sorted alphabetically for scanability.
  // Clubs store sport IDs; this turns them into the names a human picks from.
  const sportName = useSportNames();
  const sportOptions = useMemo(
    () =>
      Array.from(
        new Set(clubs.flatMap((c) => c.sports || []).filter(Boolean))
      ).sort(),
    [clubs]
  );
  const cityOptions = useMemo(
    () =>
      Array.from(
        new Set(clubs.map((c) => c.city).filter((v): v is string => !!v))
      ).sort(),
    [clubs]
  );

  const filteredAthletes = athletes.filter((athlete) => {
    const searchLower = searchTerm.toLowerCase();
    const coachName = athlete.athlete_profiles?.team?.coach?.full_name || athlete.athlete_profiles?.team?.coach?.email || "";
    const searchMatch =
      athlete.full_name?.toLowerCase().includes(searchLower) ||
      athlete.email.toLowerCase().includes(searchLower) ||
      athlete.athlete_profiles?.club?.name?.toLowerCase().includes(searchLower) ||
      coachName.toLowerCase().includes(searchLower);
    if (!searchMatch) return false;

    if (clubFilter && athlete.athlete_profiles?.club?.id !== clubFilter) return false;
    if (cityFilter && athlete.athlete_profiles?.club?.city !== cityFilter) return false;
    if (planFilter && (athlete.athlete_profiles?.plan_code || "free").toLowerCase() !== planFilter) return false;
    if (sportFilter) {
      // Prefer the team's sport_type when set, else fall back to the
      // club's sports list — matches how the "Sports" column below is
      // computed, so the filter and the visible column agree.
      const athleteSports = athlete.athlete_profiles?.team?.sport_type
        ? [athlete.athlete_profiles.team.sport_type]
        : athlete.athlete_profiles?.club?.sports || [];
      if (!athleteSports.includes(sportFilter)) return false;
    }
    return true;
  });

  // Lista vine întreagă de la server (pageSize=500), deci paginăm în client.
  // Selecția în masă rămâne peste TOT ce trece filtrele, nu doar peste pagina
  // vizibilă — altfel „selectează tot" ar însemna altceva pe fiecare pagină.
  const { page, setPage, pageSize, total, visible: pagedAthletes } = usePagination(filteredAthletes);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-slate-600 font-medium">Se încarcă...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          eyebrow="Utilizatori și membri"
          title="Sportivi"
          subtitle={<>Gestionare sportivi ({filteredAthletes.length})</>}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
            </svg>
          }
          actions={
            <>
              <AdminButton
                variant="danger"
                onClick={openDemoPurge}
                disabled={demoLoading}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>}
              >
                {demoLoading ? "Se verifică..." : "Șterge conturi demo"}
              </AdminButton>
              <AdminButton
                onClick={() => navigate("/admin/users")}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
              >
                Adaugă Sportiv Nou
              </AdminButton>
            </>
          }
        />

        {/* Search + filters */}
        <div className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-3">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Caută sportiv după nume, email, club sau antrenor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate tipurile de cont</option>
              <option value="free">Free</option>
              <option value="start">Start</option>
              <option value="campion">Campion</option>
              <option value="elita">Elite</option>
            </select>
            <select
              value={clubFilter}
              onChange={(e) => setClubFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate cluburile</option>
              {clubs
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
            </select>
            <select
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate sporturile</option>
              {sportOptions.map((s) => (
                <option key={s} value={s}>{sportName(s)}</option>
              ))}
            </select>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Toate orașele</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {(clubFilter || sportFilter || cityFilter || planFilter) && (
            <button
              onClick={() => { setClubFilter(""); setSportFilter(""); setCityFilter(""); setPlanFilter(""); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Șterge filtrele
            </button>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
            <span className="text-blue-700 font-medium">
              {selectedIds.size} sportiv(i) selectat(i)
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Deselectează
              </button>
              <button
                onClick={() => setShowBulkConfirm(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Șterge Selectate
              </button>
            </div>
          </div>
        )}

        {/* Demo-athlete purge confirmation */}
        {demoPreview && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
              <h3 className="text-xl font-bold text-slate-800 mb-2">Șterge conturi demo</h3>
              {demoPreview.count === 0 ? (
                <p className="text-slate-600 mb-6">Nu există conturi demo de sportiv de șters.</p>
              ) : (
                <>
                  <p className="text-slate-600 mb-3">
                    Se vor șterge <span className="font-semibold text-slate-800">{demoPreview.count}</span> cont(uri) demo de sportiv
                    (adrese de forma <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">athlete.…@igrowth.demo</code>). Această acțiune nu poate fi anulată.
                  </p>
                  {demoPreview.emails.length > 0 && (
                    <div className="mb-6 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-500 space-y-0.5">
                      {demoPreview.emails.slice(0, 100).map((em) => (
                        <div key={em} className="truncate">{em}</div>
                      ))}
                      {demoPreview.emails.length > 100 && (
                        <div className="text-slate-400">… și încă {demoPreview.emails.length - 100}</div>
                      )}
                    </div>
                  )}
                </>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDemoPreview(null)}
                  disabled={purging}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors disabled:opacity-50"
                >
                  {demoPreview.count === 0 ? "Închide" : "Anulează"}
                </button>
                {demoPreview.count > 0 && (
                  <button
                    onClick={confirmDemoPurge}
                    disabled={purging}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {purging ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Se șterge...
                      </>
                    ) : (
                      `Șterge ${demoPreview.count} cont(uri)`
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-xl font-bold text-slate-800 mb-2">Confirmare Ștergere</h3>
              <p className="text-slate-600 mb-6">
                Ești sigur că vrei să ștergi {selectedIds.size} sportiv(i)? Această acțiune nu poate fi anulată.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowBulkConfirm(false)}
                  disabled={bulkDeleting}
                  className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors disabled:opacity-50"
                >
                  Anulează
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {bulkDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Se șterge...
                    </>
                  ) : (
                    "Confirmă Ștergerea"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Athletes Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={filteredAthletes.length > 0 && selectedIds.size === filteredAthletes.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Nume Complet
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Sporturi
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Antrenor
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Cont
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Creat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAthletes.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>Nu există sportivi înregistrați</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedAthletes.map((athlete) => {
                    const sports = athlete.athlete_profiles?.team?.sport_type
                      ? [athlete.athlete_profiles.team.sport_type]
                      : athlete.athlete_profiles?.club?.sports || [];
                    const coachName = athlete.athlete_profiles?.team?.coach?.full_name
                      || athlete.athlete_profiles?.team?.coach?.email
                      || "-";
                    const age = computeAge(athlete.date_of_birth);
                    const plan = planMeta(athlete.athlete_profiles?.plan_code);
                    const hasParent = !!athlete.athlete_profiles?.has_parent_account;

                    return (
                      <tr
                        key={athlete.id}
                        className={`hover:bg-slate-50 transition-colors duration-150 ${selectedIds.has(athlete.id) ? 'bg-blue-50' : ''}`}
                      >
                        {/* Checkbox */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(athlete.id)}
                            onChange={() => toggleSelect(athlete.id)}
                            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                          />
                        </td>
                        {/* Full Name - Clickable */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/users/${athlete.id}`)}
                              className="text-indigo-600 hover:text-indigo-700 font-medium hover:underline transition-colors"
                            >
                              {athlete.full_name || athlete.email}
                            </button>
                            {age != null && (
                              <span className="shrink-0 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                {age} ani
                              </span>
                            )}
                          </div>
                          {(athlete.athlete_profiles?.club?.name || athlete.athlete_profiles?.club?.city) && (
                            <div className="text-xs text-slate-400 mt-0.5">
                              {[athlete.athlete_profiles?.club?.name, athlete.athlete_profiles?.club?.city].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </td>

                        {/* Sports */}
                        <td className="px-6 py-4">
                          <SportChips
                            values={sports}
                            chipClassName="px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium"
                          />
                        </td>

                        {/* Coach */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-slate-700 text-sm">{coachName}</span>
                        </td>

                        {/* Account: plan tier + parent-link indicator */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col items-start gap-1.5">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${plan.cls}`}>
                              {plan.label}
                            </span>
                            {hasParent ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
                                </svg>
                                Cont părinte
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                                Fără părinte
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Created At */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-slate-500 text-sm">
                            {formatDate(new Date(athlete.created_at))}
                          </span>
                        </td>

                        {/* Actions - Login-as + Delete */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <LoginAsButton userId={athlete.id} compact />
                            <button
                              onClick={() => handleDeleteClick(athlete.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                                deleteConfirmId === athlete.id
                                  ? "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-500/25"
                                  : "bg-red-50 text-red-600 hover:bg-red-100"
                              }`}
                            >
                              {deleteConfirmId === athlete.id ? "Confirmă Ștergere" : "Șterge"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </div>
      </div>
    </div>
  );
}
